import { z } from 'zod';

import { submittedByOverrideSchema } from './submitted-by.validator';

/**
 * Partner (Trusted Installer) application boundary schema.
 *
 * Mirrors the HubSpot form this replaces field-for-field (see the plan's §3
 * table); the option list below is the exact one the live form offered, kept
 * closed so a hand-crafted POST cannot invent expertise values the downstream
 * HubSpot property will not accept.
 *
 * The applicant's email is deliberately NOT in this schema: it comes from the
 * authenticated session, because approval grants pricing to an account and the
 * application must name the account it will price.
 */
export const PARTNER_EXPERTISE_OPTIONS = [
  'AV/home theater',
  'Electrical supply and wholesale',
  'General contractor',
  'General electrician or journeyman',
  'IT support and/or networking',
  'Low voltage electrical wiring',
  'Monitoring services',
  'PC sales, repair, system installation',
  'Physical security services',
  'Security alarm installation',
  'Security camera installation',
  'Structured cabling',
  'VoIP and/or telecom services',
] as const;

export type PartnerExpertiseOption = (typeof PARTNER_EXPERTISE_OPTIONS)[number];

const requiredText = (max: number) => z.string().trim().min(1).max(max);

/** Both agreements are must-check: `true` is the only accepted value. */
const requiredAgreement = z.literal(true, {
  errorMap: () => ({ message: 'This agreement must be accepted' }),
});

export const partnerApplicationBodySchema = z.object({
  firstName: requiredText(100),
  lastName: requiredText(100),
  mobilePhone: requiredText(50),
  company: requiredText(255),
  website: requiredText(255),
  expectedMonthlySales: requiredText(255),
  installationLocations: requiredText(2000),
  superiorSupportAndService: requiredText(5000),
  marketingOfInstallationServices: requiredText(5000),
  taxId: requiredText(100),
  licenseNumbers: requiredText(2000),
  codeOfConduct: requiredAgreement,
  resellerLicenseTaxCertificate: requiredAgreement,
  expertise: z.array(z.enum(PARTNER_EXPERTISE_OPTIONS)).max(PARTNER_EXPERTISE_OPTIONS.length).default([]),
  gclid: z.string().trim().max(255).optional().transform((value) => value || null),
});

export type PartnerApplicationBody = z.infer<typeof partnerApplicationBodySchema>;

/**
 * Admin/rep on-behalf submission: the same application, plus the customer it
 * is for. The email is looked up (or provisioned) rather than trusted as an
 * identity, so sales is never blocked by a missing storefront account.
 */
export const adminPartnerApplicationBodySchema = partnerApplicationBodySchema.extend({
  customerEmail: z.string().trim().toLowerCase().email().max(255),
  /** Rep attribution from the HubSpot card's Lambda. See the shared schema. */
  submittedBy: submittedByOverrideSchema,
});

export type AdminPartnerApplicationBody = z.infer<typeof adminPartnerApplicationBodySchema>;
